import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuth } from '@/shared/AuthContext';
import {
  type SelectiveForm, type TenantUser,
  TYPE_OPTIONS, PRIORITY_OPTIONS, inputStyle,
} from './types';

/** Modal "Nuova selettiva": form per creare e inviare la selettiva. */
export function CreateSelectiveModal({ onClose }: { onClose: () => void }): React.JSX.Element {
  const qc = useQueryClient();
  const [form, setForm] = useState<SelectiveForm>({
    type: '', priority: 'Alta', callerInfo: '', recipient: '',
    zone: '', notes: '', target: 'All',
  });
  const [showGroupModal, setShowGroupModal] = useState(false);
  // Audit techlead #5: useAuth invece di localStorage diretto.
  // Cosi' un utente promosso/declassato durante la sessione vede l'UI
  // aggiornata senza reload (es. opzione 'Gruppo scelto' che appare/sparisce).
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Amministratore', 'SuperAdmin');
  const mut = useMutation({
    mutationFn: async () => {
      await api.post('/selectives', {
        ...form,
        customUserIds: form.target === 'Custom' ? (form.customUserIds ?? []) : undefined,
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['selectives.active'] }); onClose(); },
  });

  const canSubmit = form.type.trim().length > 0
    && (form.target !== 'Custom' || (form.customUserIds?.length ?? 0) > 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <h2 style={{ marginBottom: 18 }}>🚨 Nuova Selettiva</h2>

        <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}>
          {TYPE_OPTIONS.map(o => (
            <option key={o.value} value={o.value} disabled={o.placeholder}>{o.label}</option>
          ))}
        </select>

        <div style={{ margin: '15px 0' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 10 }}>Priorità:</label>
          <div style={{ display: 'flex', gap: 10 }}>
            {PRIORITY_OPTIONS.map(p => {
              const checked = form.priority === p.value;
              return (
                <label key={p.value}
                  style={{
                    flex: 1, padding: 10, border: `2px solid ${p.color}`, borderRadius: 8,
                    cursor: 'pointer', textAlign: 'center', userSelect: 'none',
                    background: checked ? `${p.color}22` : 'transparent',
                  }}>
                  <input type="radio" name="priority" value={p.value}
                    checked={checked}
                    onChange={() => setForm({ ...form, priority: p.value })}
                    style={{ marginRight: 5 }} />
                  <span style={{ color: p.color, fontWeight: 'bold' }}>{p.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <input type="text" placeholder="Chiamante" value={form.callerInfo}
          onChange={e => setForm({ ...form, callerInfo: e.target.value })} style={inputStyle} />
        <input type="text" placeholder="Destinatario" value={form.recipient}
          onChange={e => setForm({ ...form, recipient: e.target.value })} style={inputStyle} />
        <input type="text" placeholder="Zona" value={form.zone}
          onChange={e => setForm({ ...form, zone: e.target.value })} style={inputStyle} />
        <textarea placeholder="Note aggiuntive" value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          style={{ ...inputStyle, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} />

        <div style={{ margin: '15px 0' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 10 }}>Invia a:</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 30%', cursor: 'pointer' }}>
              <input type="radio" name="target" value="All"
                checked={form.target === 'All'}
                onChange={() => setForm({ ...form, target: 'All', customUserIds: undefined })}
                style={{ marginRight: 6 }} />
              Tutti gli utenti operativi
            </label>
            <label style={{ flex: '1 1 30%', cursor: 'pointer' }}>
              <input type="radio" name="target" value="Available"
                checked={form.target === 'Available'}
                onChange={() => setForm({ ...form, target: 'Available', customUserIds: undefined })}
                style={{ marginRight: 6 }} />
              Solo disponibili
            </label>
            {isAdmin && (
              <label style={{ flex: '1 1 30%', cursor: 'pointer' }}>
                <input type="radio" name="target" value="Custom"
                  checked={form.target === 'Custom'}
                  onChange={() => setForm({ ...form, target: 'Custom' })}
                  style={{ marginRight: 6 }} />
                Gruppo scelto
              </label>
            )}
          </div>
          {form.target === 'Custom' && isAdmin && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setShowGroupModal(true)}
                style={{ padding: '8px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
                         background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)', color: 'white', fontWeight: 600 }}>
                {form.customUserIds && form.customUserIds.length > 0 ? '✎ Modifica gruppo' : '+ Crea gruppo'}
              </button>
              <span style={{ fontSize: 13, color: '#666' }}>
                {form.customUserIds && form.customUserIds.length > 0
                  ? `${form.customUserIds.length} utenti selezionati`
                  : 'Nessun utente selezionato'}
              </span>
            </div>
          )}
        </div>

        <button onClick={() => mut.mutate()} disabled={!canSubmit || mut.isPending}
          style={{
            width: '100%', padding: 14, marginTop: 10, fontSize: 16, fontWeight: 'bold',
            border: 'none', borderRadius: 8, cursor: canSubmit ? 'pointer' : 'not-allowed',
            background: canSubmit ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)' : '#aaa',
            color: 'white',
          }}>
          {mut.isPending ? 'Invio...' : '📤 Invia Selettiva'}
        </button>
        <button onClick={onClose} disabled={mut.isPending}
          style={{
            width: '100%', padding: 12, marginTop: 8, fontSize: 16, fontWeight: 'bold',
            border: 'none', borderRadius: 8, cursor: 'pointer',
            background: '#6c757d', color: 'white',
          }}>
          Annulla
        </button>
      </div>
      {showGroupModal && (
        <CustomGroupModal
          selected={form.customUserIds ?? []}
          onSave={ids => { setForm({ ...form, customUserIds: ids }); setShowGroupModal(false); }}
          onClose={() => setShowGroupModal(false)}
        />
      )}
    </div>
  );
}

/**
 * Sub-modal di CreateSelectiveModal: lista utenti tenant con multi-check
 * per costruire un target Custom. Visibile solo per admin.
 */
function CustomGroupModal({ selected, onSave, onClose }: {
  selected: string[];
  onSave: (ids: string[]) => void;
  onClose: () => void;
}): React.JSX.Element {
  const { data: users } = useQuery({
    queryKey: ['selective.target-users'],
    queryFn: async () => (await api.get('/selectives/target-users')).data as TenantUser[],
  });
  const [pick, setPick] = useState<Set<string>>(new Set(selected));
  const toggle = (id: string) => {
    const n = new Set(pick);
    if (n.has(id)) n.delete(id); else n.add(id);
    setPick(n);
  };
  const operatives = (users ?? []).filter(u => u.isOperative);
  const allOp = operatives.map(u => u.id);
  const allSel = allOp.length > 0 && allOp.every(id => pick.has(id));
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}
        style={{ maxWidth: 500, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ margin: '0 0 12px' }}>👥 Seleziona utenti del gruppo</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 13, color: '#666' }}>
            {pick.size} su {operatives.length} operativi
          </span>
          <button type="button" onClick={() => setPick(allSel ? new Set() : new Set(allOp))}
            style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #ced4da',
                     borderRadius: 4, background: '#f8f9fa', cursor: 'pointer' }}>
            {allSel ? 'Deseleziona tutti' : 'Seleziona tutti'}
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 6, padding: 6 }}>
          {operatives.map(u => (
            <label key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
              borderRadius: 4, cursor: 'pointer',
              background: pick.has(u.id) ? '#e7f3ff' : 'transparent',
            }}>
              <input type="checkbox" checked={pick.has(u.id)} onChange={() => toggle(u.id)} />
              <span><strong>{u.nickname}</strong> <span style={{ color: '#888', fontSize: 12 }}>({u.email})</span></span>
            </label>
          ))}
          {operatives.length === 0 && (
            <p style={{ color: '#888', textAlign: 'center', padding: 20 }}>
              Nessun utente operativo nel distaccamento
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button type="button" onClick={onClose}
            style={{ flex: 1, padding: 10, border: 'none', borderRadius: 6, cursor: 'pointer',
                     background: '#6c757d', color: 'white', fontWeight: 600 }}>
            Annulla
          </button>
          <button type="button" onClick={() => onSave([...pick])}
            disabled={pick.size === 0}
            style={{ flex: 1, padding: 10, border: 'none', borderRadius: 6,
                     cursor: pick.size > 0 ? 'pointer' : 'not-allowed',
                     background: pick.size > 0 ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)' : '#aaa',
                     color: 'white', fontWeight: 600 }}>
            ✓ Conferma ({pick.size})
          </button>
        </div>
      </div>
    </div>
  );
}
